import { compare as dateCompare, isAfter } from 'utils/date';
import { compare as stringCompare } from 'utils/string';
import { compare as booleanCompare } from 'utils/boolean';
import { headerKeys } from './constants';
import { TradeshowStatus } from 'models/tradeshowStatus';

/**
 * Compares two tradeshow objects for sorting, using a primary key and direction,
 * with a defined sequence of tie-breakers for consistent ordering.
 *
 * Sort precedence:
 * - Primary: The specified `key` (sorted by `dir` if applicable)
 *   - For name: string comparison by direction
 *   - For tradeshowStatus/startDate: date comparison by direction, then endDate
 *   - For location: country, then state, then city (all by direction)
 *   - For platformStatus/registeredInterest: boolean comparison by direction
 *   - For contactAccess: order 1 = estimated date (sorted by date), 2 = live, 3 = coming soon 4 = not available
 * - Tie-breakers (applied in order):
 *   1. startDate (ascending)
 *   2. endDate (ascending)
 *   3. name (ascending)
 *
 * @param {Object} a - The first tradeshow object to compare.
 * @param {Object} b - The second tradeshow object to compare.
 * @param {string} key - The primary key to sort by (should match one of the headerKeys).
 * @param {'asc'|'desc'} dir - The direction to sort ('asc' or 'desc') for the primary key only.
 * @returns {number} Negative if `a` should come before `b`, positive if after, 0 if equal.
 */
const sort = (a, b, key, dir) => {
  let aValue, bValue;
  var now = new Date().toString();

  const getTradeshowStatusOrderNumber = tradeshow => {
    if (tradeshow.statusId === 3) {
      return 5; // Cancelled
    } else if (tradeshow.statusId === 2) {
      return 4; // Postponed
    } else if (tradeshow.startDate && tradeshow.endDate) {
      if (isAfter(now, tradeshow.endDate)) {
        return 3; // Completed
      } else if (isAfter(tradeshow.startDate, now)) {
        return 1; // Upcoming
      } else {
        return 2; // Ongoing
      }
    } else {
      return 6; // Fallback to TBC
    }
  };

  // Priority: 1 = has estimated availability date, 2 = live (View Contacts), 3 = Coming Soon, 4 = Not Available
  const getContactAccessOrderNumber = tradeshow => {
    if (tradeshow.availableToDownload) return 2;
    if (tradeshow.statusId === TradeshowStatus.Cancelled) return 4;
    if (dateCompare(now, tradeshow.startDate, 'd') >= 0) return 3;
    if (tradeshow.estimatedAvailabilityDate != null) return 1;
    return 3;
  };

  const getRegisteredInterestOrderNumber = tradeshow => {
    // "Export Tradeshow Contacts"
    if (tradeshow.availableToDownload && !tradeshow.isUserRegistered) {
      // "Export Tradeshow Contacts" - not registered
      // This ensures that the un-registered "available to download" tradeshows
      // which show the "Export Tradeshow Contacts" button always come last
      return dir === 'asc' ? 6 : 1;
    } else if (tradeshow.availableToDownload && tradeshow.isUserRegistered) {
      // "Export Tradeshow Contacts" - registered
      // This ensures that the "previously registered" and "available to download"
      // tradeshows which show the "Export Tradeshow Contacts" button always come second last
      return dir === 'asc' ? 5 : 2;
    } else if (tradeshow.isUserRegistered) {
      // "Currently Registered Interest"
      // This will always come either first or second
      return 3;
    } else {
      // "Not Registered"
      // This will always come either first or second
      return 4;
    }
  };

  // Map certain keys to their actual data fields
  switch (key) {
    case headerKeys.contactAccess:
      aValue = getContactAccessOrderNumber(a);
      bValue = getContactAccessOrderNumber(b);
      break;
    case headerKeys.platformStatus:
      aValue = a.availableToDownload;
      bValue = b.availableToDownload;
      break;
    case headerKeys.registeredInterest:
      aValue = getRegisteredInterestOrderNumber(a);
      bValue = getRegisteredInterestOrderNumber(b);
      break;
    case headerKeys.tradeshowStatus:
      aValue = getTradeshowStatusOrderNumber(a);
      bValue = getTradeshowStatusOrderNumber(b);
      break;
    default:
      aValue = a[key];
      bValue = b[key];
      break;
  }

  // Location sorting is done by region, country, state, city, virtual, tbc
  const locationCompareres = [
    (a, b) => stringCompare(a.country, b.country),
    (a, b) => stringCompare(a.state, b.state),
    (a, b) => stringCompare(a.city, b.city),
    (a, b) => booleanCompare(!!b.isVirtual, !!a.isVirtual),
    (a, b) => booleanCompare(!!b.isTbc, !!a.isTbc),
  ];

  // All in order of precedence (these are the tie-breakers after primary sort)
  const allCompareres = [
    (a, b) => dateCompare(a.startDate, b.startDate, 'd'),
    (a, b) => dateCompare(a.endDate, b.endDate, 'd'),
    (a, b) => stringCompare(a.name, b.name),
  ];

  let primaryCompare;
  switch (key) {
    case headerKeys.registeredInterest:
    case headerKeys.tradeshowStatus:
      primaryCompare =
        dir === 'asc'
          ? stringCompare(aValue, bValue, false) // false for numeric comparison (slight hack)
          : stringCompare(bValue, aValue, false);
      break;
    case headerKeys.name:
      primaryCompare =
        dir === 'asc'
          ? stringCompare(aValue, bValue)
          : stringCompare(bValue, aValue);
      break;
    case headerKeys.startDate:
      primaryCompare =
        dir === 'asc'
          ? dateCompare(a.startDate, b.startDate, 'd')
          : dateCompare(b.startDate, a.startDate, 'd');
      if (primaryCompare === 0) {
        primaryCompare =
          dir === 'asc'
            ? dateCompare(a.endDate, b.endDate, 'd')
            : dateCompare(b.endDate, a.endDate, 'd');
      }
      break;
    case headerKeys.location:
      for (const locationComparer of locationCompareres) {
        primaryCompare =
          dir === 'asc' ? locationComparer(a, b) : locationComparer(b, a);
        if (primaryCompare !== 0) break;
      }
      break;
    case headerKeys.contactAccess:
      primaryCompare =
        dir === 'asc'
          ? stringCompare(aValue, bValue, false)
          : stringCompare(bValue, aValue, false);
      // Within the estimated-dates group, sort by the estimated date itself
      if (primaryCompare === 0 && aValue === 1) {
        primaryCompare =
          dir === 'asc'
            ? dateCompare(
                a.estimatedAvailabilityDate,
                b.estimatedAvailabilityDate,
                'd'
              )
            : dateCompare(
                b.estimatedAvailabilityDate,
                a.estimatedAvailabilityDate,
                'd'
              );
      }
      break;
    case headerKeys.platformStatus:
      primaryCompare =
        dir === 'desc'
          ? booleanCompare(!!aValue, !!bValue)
          : booleanCompare(!!bValue, !!aValue);
      break;
    default:
      primaryCompare = 0;
  }

  if (primaryCompare !== 0) return primaryCompare;

  // Apply the rest of the tie-breakers in order
  for (const comparer of allCompareres) {
    const result = comparer(a, b);
    if (result !== 0) return result;
  }

  return 0;
};

export default sort;
